import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import MetricCard from './components/MetricCard';
import APIStatusPanel from './components/APIStatusPanel';
import RecentConsultationsTable from './components/RecentConsultationsTable';
import QuickActionsPanel from './components/QuickActionsPanel';
import FilterToolbar from './components/FilterToolbar';
import type { 
  ConsultationMetric, 
  RecentConsultation, 
  APIStatus, 
  DashboardFilters,
  FilterPreset 
} from './types';

const TaxConsultationDashboard = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [filters, setFilters] = useState<DashboardFilters>({
    dateRange: { start: null, end: null },
    status: [],
    searchTerm: ''
  });

  const metrics: ConsultationMetric[] = [
    {
      id: '1',
      label: 'Consultas Hoje',
      value: 47,
      change: 12.5,
      changeLabel: 'vs. ontem',
      icon: 'FileText',
      color: 'var(--color-primary)'
    },
    {
      id: '2',
      label: 'Taxa de Sucesso',
      value: '98.2%',
      change: 2.1,
      changeLabel: 'vs. semana anterior',
      icon: 'CheckCircle2',
      color: 'var(--color-success)'
    },
    {
      id: '3',
      label: 'Tempo Médio',
      value: '2.4s',
      change: -8.3,
      changeLabel: 'vs. média mensal',
      icon: 'Clock',
      color: 'var(--color-accent)'
    },
    {
      id: '4',
      label: 'Produtos Consultados',
      value: 1247,
      change: 18.7,
      changeLabel: 'vs. semana anterior',
      icon: 'Package',
      color: 'var(--color-warning)'
    }
  ];

  const apiStatus: APIStatus = {
    isConnected: true,
    lastSync: new Date(Date.now() - 180000),
    responseTime: 342,
    errorCount: 2
  };

  const recentConsultations: RecentConsultation[] = [
    {
      id: 'c001',
      companyName: 'Indústria Metalúrgica São Paulo Ltda',
      cnpj: '12.345.678/0001-90',
      consultationDate: new Date(Date.now() - 3600000),
      status: 'success',
      statusLabel: 'Concluída',
      productCount: 15,
      totalTax: 45678.90
    },
    {
      id: 'c002',
      companyName: 'Distribuidora Nacional de Alimentos S.A.',
      cnpj: '23.456.789/0001-01',
      consultationDate: new Date(Date.now() - 7200000),
      status: 'success',
      statusLabel: 'Concluída',
      productCount: 32,
      totalTax: 89234.56
    },
    {
      id: 'c003',
      companyName: 'Comércio de Eletrônicos Rio de Janeiro',
      cnpj: '34.567.890/0001-12',
      consultationDate: new Date(Date.now() - 10800000),
      status: 'warning',
      statusLabel: 'Com Avisos',
      productCount: 8,
      totalTax: 23456.78
    },
    {
      id: 'c004',
      companyName: 'Importadora Global Trading Ltda',
      cnpj: '45.678.901/0001-23',
      consultationDate: new Date(Date.now() - 14400000),
      status: 'error',
      statusLabel: 'Erro API',
      productCount: 45,
      totalTax: 0
    },
    {
      id: 'c005',
      companyName: 'Farmacêutica Brasileira S.A.',
      cnpj: '56.789.012/0001-34',
      consultationDate: new Date(Date.now() - 18000000),
      status: 'success',
      statusLabel: 'Concluída',
      productCount: 67,
      totalTax: 156789.45
    },
    {
      id: 'c006',
      companyName: 'Têxtil Nordeste Indústria e Comércio',
      cnpj: '67.890.123/0001-45',
      consultationDate: new Date(Date.now() - 21600000),
      status: 'success',
      statusLabel: 'Concluída',
      productCount: 23,
      totalTax: 67890.12
    },
    {
      id: 'c007',
      companyName: 'Construção Civil Minas Gerais Ltda',
      cnpj: '78.901.234/0001-56',
      consultationDate: new Date(Date.now() - 25200000),
      status: 'pending',
      statusLabel: 'Processando',
      productCount: 12,
      totalTax: 0
    },
    {
      id: 'c008',
      companyName: 'Agropecuária Sul Brasil S.A.',
      cnpj: '89.012.345/0001-67',
      consultationDate: new Date(Date.now() - 28800000),
      status: 'success',
      statusLabel: 'Concluída',
      productCount: 89,
      totalTax: 234567.89
    },
    {
      id: 'c009',
      companyName: 'Tecnologia e Inovação Digital Ltda',
      cnpj: '90.123.456/0001-78',
      consultationDate: new Date(Date.now() - 32400000),
      status: 'success',
      statusLabel: 'Concluída',
      productCount: 5,
      totalTax: 12345.67
    },
    {
      id: 'c010',
      companyName: 'Logística Integrada Transportes S.A.',
      cnpj: '01.234.567/0001-89',
      consultationDate: new Date(Date.now() - 36000000),
      status: 'warning',
      statusLabel: 'Com Avisos',
      productCount: 34,
      totalTax: 78901.23
    },
    {
      id: 'c011',
      companyName: 'Química Industrial Paraná Ltda',
      cnpj: '11.234.567/0001-90',
      consultationDate: new Date(Date.now() - 39600000),
      status: 'success',
      statusLabel: 'Concluída',
      productCount: 56,
      totalTax: 145678.90
    },
    {
      id: 'c012',
      companyName: 'Varejo Nacional Supermercados S.A.',
      cnpj: '22.345.678/0001-01',
      consultationDate: new Date(Date.now() - 43200000),
      status: 'success',
      statusLabel: 'Concluída',
      productCount: 123,
      totalTax: 345678.12
    },
    {
      id: 'c013',
      companyName: 'Automotiva Centro-Oeste Peças Ltda',
      cnpj: '33.456.789/0001-12',
      consultationDate: new Date(Date.now() - 46800000),
      status: 'error',
      statusLabel: 'Erro Validação',
      productCount: 28,
      totalTax: 0
    },
    {
      id: 'c014',
      companyName: 'Energia Renovável Brasil S.A.',
      cnpj: '44.567.890/0001-23',
      consultationDate: new Date(Date.now() - 50400000),
      status: 'success',
      statusLabel: 'Concluída',
      productCount: 41,
      totalTax: 98765.43
    },
    {
      id: 'c015',
      companyName: 'Cosméticos e Perfumaria Nacional Ltda',
      cnpj: '55.678.901/0001-34',
      consultationDate: new Date(Date.now() - 54000000),
      status: 'success',
      statusLabel: 'Concluída',
      productCount: 76,
      totalTax: 187654.32
    }
  ];

  const filterPresets: FilterPreset[] = [
    {
      id: 'preset1',
      name: 'Hoje',
      filters: {
        dateRange: {
          start: new Date(new Date().setHours(0, 0, 0, 0)),
          end: new Date(new Date().setHours(23, 59, 59, 999))
        }
      }
    },
    {
      id: 'preset2',
      name: 'Última Semana',
      filters: {
        dateRange: {
          start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
          end: new Date()
        }
      }
    },
    {
      id: 'preset3',
      name: 'Apenas Erros',
      filters: {
        status: ['error']
      }
    }
  ];

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.altKey) {
        switch (e.key.toLowerCase()) {
          case 'n':
            e.preventDefault();
            navigate('/new-tax-consultation-form');
            break;
          case 'h':
            e.preventDefault();
            navigate('/consultation-history');
            break;
          case 's':
            e.preventDefault();
            document.querySelector<HTMLInputElement>('input[type="search"]')?.focus();
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [navigate]);

  const handleReExecute = (id: string) => {
    console.log(`Reexecutando consulta ${id}`);
  };

  const handleApplyPreset = (preset: FilterPreset) => {
    setFilters({
      ...filters,
      ...preset.filters
    });
  };

  const filteredConsultations = recentConsultations.filter(consultation => {
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      if (
        !consultation.companyName.toLowerCase().includes(searchLower) &&
        !consultation.cnpj.includes(searchLower)
      ) {
        return false;
      }
    }

    if (filters.status.length > 0 && !filters.status.includes(consultation.status)) {
      return false;
    }

    if (filters.dateRange.start && consultation.consultationDate < filters.dateRange.start) {
      return false;
    }

    if (filters.dateRange.end && consultation.consultationDate > filters.dateRange.end) {
      return false;
    }

    return true;
  });

  return (
    <div className="min-h-screen bg-background">
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onCollapsedChange={setSidebarCollapsed} 
      />
      
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <Header 
          title="Dashboard de Consultas Tributárias"
          showUserMenu
          onUserMenuClick={() => {}}
        />
        
        <main className="pt-16 p-6">
          <div className="max-w-[1600px] mx-auto space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {metrics.map((metric) => (
                <MetricCard key={metric.id} metric={metric} />
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <FilterToolbar
                  filters={filters}
                  onFiltersChange={setFilters}
                  presets={filterPresets}
                  onApplyPreset={handleApplyPreset}
                />
                
                <RecentConsultationsTable
                  consultations={filteredConsultations}
                  onReExecute={handleReExecute}
                />
              </div>

              <div className="space-y-6">
                <APIStatusPanel status={apiStatus} />
                <QuickActionsPanel />
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default TaxConsultationDashboard;